import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  TextField,
  Alert,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Stack,
  SvgIcon,
  useTheme,
  useMediaQuery,
  Autocomplete,
  Chip,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { ArrowLeft } from '../../../assets';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { complianceService } from '../../../app/services/api/compliance/complianceService';
import { UserContext } from '../../../components/shared/useUser';
import { AuditTemplate, AuditSection, AuditQuestion, AuditTemplateFranchise, ComplianceFranchise } from '../../../pages/Compliance/Interfaces/complicanceInterfaces';
import { ActionTriggerType, QuestionType, TriggerCondition } from '../../../pages/Compliance/Enums/complianceEnums';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';

const AuditTemplateBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { templateId } = useParams<{ templateId?: string }>();
  const { t } = useTranslation('complianceAudits');
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { selectedStore, hasPermissionTo, userFranchisesCurrentTenant } = useContext(UserContext);
  const canEdit = hasPermissionTo(['AuditTemplatesEdit']);
  const canCreate = hasPermissionTo(['AuditTemplatesCreate']);
  const canSave = templateId ? canEdit : canCreate;

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tenantFranchises, setTenantFranchises] = useState<ComplianceFranchise[]>([]);

  const [template, setTemplate] = useState<Partial<AuditTemplate>>({
    name: '',
    description: '',
    franchises: [],
  });
  
  const [selectedFranchiseIds, setSelectedFranchiseIds] = useState<number[]>([]);
  const [allFranchisesSelected, setAllFranchisesSelected] = useState(false);
  
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<AuditSection | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<AuditQuestion | null>(null);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState<number>(-1);
  const [currentSectionIndex, setCurrentSectionIndex] = useState<number>(-1);

  useEffect(() => {
    loadTenantFranchises();
    if (templateId) {
      loadTemplate();
    }
  }, [templateId, selectedStore?.storeNumber]);

  const loadTenantFranchises = () => {
    const data: ComplianceFranchise[] = userFranchisesCurrentTenant
      .map(f => ({ franchiseId: f.franchiseId as number, franchiseName: f.franchiseName ?? '' }));
    setTenantFranchises(data);
  };

  const loadTemplate = async () => {
    try {
      const data = await complianceService.auditTemplates.getById(parseInt(templateId!), selectedStore?.storeNumber);
      const franchisesData: ComplianceFranchise[] = userFranchisesCurrentTenant
        .map(f => ({ franchiseId: f.franchiseId as number, franchiseName: f.franchiseName ?? '' }));

      setTenantFranchises(franchisesData);

      // Sort sections and questions by displayOrder
      if (data.sections) {
        data.sections.sort((a: AuditSection, b: AuditSection) => a.displayOrder - b.displayOrder);
        data.sections.forEach((section: AuditSection) => {
          if (section.questions) {
            section.questions.sort((a: AuditQuestion, b: AuditQuestion) => a.displayOrder - b.displayOrder);
          }
        });
      }
      setTemplate(data);

      // Populate franchise selection state
      if (data.franchises && data.franchises.length > 0) {
        const ids = data.franchises.map((f: AuditTemplateFranchise) => f.franchiseId);
        setSelectedFranchiseIds(ids);
        setAllFranchisesSelected(franchisesData.length > 0 && ids.length === franchisesData.length);
      } else {
        setSelectedFranchiseIds([]);
        setAllFranchisesSelected(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('templateBuilder.failedToLoadTemplate'));
    }
  };

  const normalizeTemplateForSave = (
    template: AuditTemplate,
  ): AuditTemplate => {
    const sections = template.sections?.map(section => ({
      ...section,
      id: section.id ?? 0,
      questions: section.questions?.map(({ isNew, options, ...q }) => ({
        ...q,
        id: isNew ? 0 : q.id,
        options: options?.map(({ isNew: optionIsNew, ...opt }) => ({
          ...opt,
          id: optionIsNew ? 0 : opt.id,
        })),
      })),
    }));

    const activeQuestions = (sections ?? [])
      .flatMap(s => s.questions ?? []);

    return {
      ...template,
      totalQuestions: activeQuestions.length,
      totalPossibleScore: activeQuestions.reduce((sum, q) => sum + (q.maxScore ?? 0), 0),
      franchises: selectedFranchiseIds.map(id => {
        const franchise = tenantFranchises.find(f => f.franchiseId === id);
        return {
          franchiseId: id,
          franchiseName: franchise?.franchiseName || '',
        };
      }),
      sections,
    };
  };

  const handleSaveTemplate = async () => {
    if (!template.name?.trim()) {
      setError(t('templateBuilder.templateNameRequired'));
      return;
    }

    if (selectedFranchiseIds.length === 0) {
      setError(t('templateBuilder.franchiseRequired'));
      return;
    }

    // Check for duplicate template name
    try {
      const allTemplates = await complianceService.auditTemplates.getAll(undefined, true, selectedStore?.storeNumber);
      const duplicateExists = allTemplates.some(
        (t) => t.name.toLowerCase().trim() === template.name!.toLowerCase().trim()
          && t.id !== (templateId ? parseInt(templateId) : undefined),
      );
      if (duplicateExists) {
        setError(t('templateBuilder.duplicateName'));
        return;
      }
    } catch {
      // If we can't check, proceed with save and let the backend handle it
    }

    for (const section of template.sections || []) {
      for (const question of section.questions || []) {
        if (!question.actionTriggers?.length) continue;
        for (let i = 0; i < question.actionTriggers.length; i++) {
          const trigger = question.actionTriggers[i];
          if (
            question.questionType === QuestionType.SingleChoice ||
            question.questionType === QuestionType.MultipleChoice
          ) {
            const validOptionTexts = (question.options || []).map((o) => o.optionText);
            if (!validOptionTexts.includes(trigger.triggerValue as string)) {
              setError(
                t('templateBuilder.templateTriggerInvalidValue', { number: i + 1, question: question.questionText }),
              );
              return;
            }
          } else if (question.questionType === QuestionType.Boolean) {
            const val = trigger.triggerValue;
            if (val !== true && val !== false && val !== 'true' && val !== 'false') {
              setError(
                t('templateBuilder.templateTriggerMustBeYesNo', { number: i + 1, question: question.questionText }),
              );
              return;
            }
          }
        }
      }
    }

    try {
      setSaving(true);
      setError(null);

      if (templateId) {
        await complianceService.auditTemplates.update(parseInt(templateId), normalizeTemplateForSave(template as AuditTemplate), selectedStore?.storeNumber);
        setSuccess(t('templateBuilder.templateUpdated'));
      } else {
        const normalized = normalizeTemplateForSave(template as AuditTemplate);
        const created = await complianceService.auditTemplates.create(normalized as Omit<AuditTemplate, 'id' | 'createdDate' | 'publishedDate'>, selectedStore?.storeNumber);
        setSuccess(t('templateBuilder.templateCreated'));
        navigate(`/compliance/audits/templates/builder/${created.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('templateBuilder.failedToSaveTemplate'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddSection = () => {
    setEditingSection({
      name: '',
      description: '',
      displayOrder: template.sections?.length || 0,
    } as AuditSection);
    setSectionDialogOpen(true);
  };

  const handleEditSection = (index: number) => {
    setEditingSection(template.sections![index]);
    setCurrentSectionIndex(index);
    setSectionDialogOpen(true);
  };

  const handleSaveSection = () => {
    if (!editingSection?.name.trim()) {
      setError(t('templateBuilder.sectionNameRequired'));
      return;
    }

    const sections = [...(template.sections || [])];
    if (currentSectionIndex >= 0) {
      sections[currentSectionIndex] = editingSection;
    } else {
      sections.push(editingSection);
    }

    setTemplate({ ...template, sections });
    setSectionDialogOpen(false);
    setEditingSection(null);
    setCurrentSectionIndex(-1);
  };

  const handleDeleteSection = (index: number) => {
    const sections = template.sections?.filter((_, i) => i !== index) || [];
    setTemplate({ ...template, sections });
  };

  const handleAddQuestion = (sectionIndex: number) => {
    setCurrentSectionIndex(sectionIndex);
    setEditingQuestionIndex(-1);
    setEditingQuestion({
      id: 0,
      isNew: true,
      sectionId: 0,
      questionText: '',
      questionType: QuestionType.Text,
      isRequired: false,
      displayOrder: 0,
      maxScore: null,
      helpText: '',
      options: [],
      actionTriggers: [],
    } as AuditQuestion);
    setQuestionDialogOpen(true);
  };

  const handleEditQuestion = (sectionIndex: number, questionIndex: number) => {
    setCurrentSectionIndex(sectionIndex);
    setEditingQuestionIndex(questionIndex);
    const question = template.sections![sectionIndex].questions![questionIndex];
    setEditingQuestion({ ...question, options: question.options ? [...question.options.map(o => ({ ...o }))] : [], actionTriggers: question.actionTriggers ? [...question.actionTriggers.map(t => ({ ...t }))] : [] });
    setQuestionDialogOpen(true);
  };

  const handleSaveQuestion = () => {
    if (!editingQuestion?.questionText.trim()) {
      setError(t('templateBuilder.questionTextRequired'));
      return;
    }

    // Validate action trigger values match available answers
    if (editingQuestion.actionTriggers && editingQuestion.actionTriggers.length > 0) {
      for (let i = 0; i < editingQuestion.actionTriggers.length; i++) {
        const trigger = editingQuestion.actionTriggers[i];
        const triggerLabel = `Action Trigger ${i + 1}`;

        if (editingQuestion.questionType === QuestionType.Boolean) {
          const val = trigger.triggerValue;
          if (val !== true && val !== false && val !== 'true' && val !== 'false') {
            setError(`${triggerLabel}: ${t('templateBuilder.triggerValueMustBeYesNo', { number: '' }).trim()}`);
            return;
          }
        } else if (
          editingQuestion.questionType === QuestionType.SingleChoice ||
          editingQuestion.questionType === QuestionType.MultipleChoice
        ) {
          const validOptionTexts = (editingQuestion.options || []).map((o) => o.optionText);
          if (!validOptionTexts.includes(trigger.triggerValue as string)) {
            setError(`${triggerLabel}: ${t('templateBuilder.triggerValueMustMatchOption', { number: '' }).trim()}`);
            return;
          }
        }
      }
    }

    const sections = [...(template.sections || [])];
    const section = { ...sections[currentSectionIndex] };

    if (!section.questions) {
      section.questions = [];
    } else {
      section.questions = [...section.questions];
    }

    if (editingQuestionIndex >= 0) {
      // Update existing question at its known index
      section.questions[editingQuestionIndex] = {
        ...editingQuestion,
        sectionId: section.id || 0,
      };
    } else {
      // Add new question with unique ID
      const newQuestion = {
        ...editingQuestion,
        id: Date.now(), // Temporary ID for client-side
        isNew: true,
        sectionId: section.id || 0,
        displayOrder: section.questions.length,
      };
      section.questions.push(newQuestion);
    }

    sections[currentSectionIndex] = section;
    setTemplate({ ...template, sections });
    setQuestionDialogOpen(false);
    setEditingQuestion(null);
    setEditingQuestionIndex(-1);
    setCurrentSectionIndex(-1);
  };

  const handleDeleteQuestion = (sectionIndex: number, questionIndex: number) => {
    const sections = [...(template.sections || [])];
    const section = sections[sectionIndex];

    if (section.questions) {
      section.questions = section.questions.filter((_: AuditQuestion, i: number) => i !== questionIndex);
      // Update display orders
      section.questions.forEach((q: AuditQuestion, i: number) => {
        q.displayOrder = i;
      });
    }

    sections[sectionIndex] = section;
    setTemplate({ ...template, sections });
  };

  // Drag and drop handlers
  const handleSectionDragEnd = (result: DropResult) => {
    if (!result.destination || !template.sections) {
      return;
    }

    const sections = Array.from(template.sections);
    const [reorderedSection] = sections.splice(result.source.index, 1);
    sections.splice(result.destination.index, 0, reorderedSection);

    // Update display orders
    sections.forEach((section, index) => {
      section.displayOrder = index;
    });

    setTemplate({ ...template, sections });
  };

  const handleQuestionDragEnd = (sectionIndex: number) => (result: DropResult) => {
    if (!result.destination || !template.sections) {
      return;
    }

    const sections = [...template.sections];
    const section = sections[sectionIndex];

    if (!section.questions) {
      return;
    }

    const questions = Array.from(section.questions);
    const [reorderedQuestion] = questions.splice(result.source.index, 1);
    questions.splice(result.destination.index, 0, reorderedQuestion);

    // Update display orders
    questions.forEach((question, index) => {
      question.displayOrder = index;
    });

    section.questions = questions;
    sections[sectionIndex] = section;
    setTemplate({ ...template, sections });
  };

  return (
    <PageArea>
      {/* Header */}
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box width={isMobile ? '100%' : 'auto'}>
          <PageTitle subTitle={t('templateBuilder.pageSubTitle')}>
            {templateId ? t('templateBuilder.editPageTitle') : t('templateBuilder.createPageTitle')}
          </PageTitle>
        </Box>

        <Stack
          direction="row"
          spacing={2}
          width={isMobile ? '100%' : 'auto'}
          sx={{
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
          }}
        >
          <Button
            variant="secondary"
            size="lg"
            startIcon={<SvgIcon><ArrowLeft /></SvgIcon>}
            onClick={() => navigate('/compliance/audits/templates')}
          >
            {t('common.back')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            startIcon={<SaveIcon />}
            onClick={handleSaveTemplate}
            disabled={saving || !canSave}
          >
            {t('templateBuilder.saveTemplate')}
          </Button>
        </Stack>
      </Stack>

      {/* Alerts */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          onClose={() => setSuccess(null)}
          sx={{ mb: 2 }}
        >
          {success}
        </Alert>
      )}

      {/* Template Details */}
      <Section style={{ p: 5, pt: 8, mb: 5, pb: 0 }}>
        <TextField
          fullWidth
          label={t('templateBuilder.templateName')}
          value={template.name}
          onChange={(e) => setTemplate({ ...template, name: e.target.value })}
          required
        />
        <TextField
          fullWidth
          multiline
          rows={3}
          label={t('common.description')}
          value={template.description || ''}
          onChange={(e) => setTemplate({ ...template, description: e.target.value })}
        />
        <Autocomplete
          multiple
          options={tenantFranchises}
          disableCloseOnSelect
          getOptionLabel={(option) => option.franchiseName || ''}
          value={tenantFranchises.filter(f => selectedFranchiseIds.includes(f.franchiseId))}
          isOptionEqualToValue={(option, value) => option.franchiseId === value.franchiseId}
          onChange={(_event, newValue) => {
            const ids = newValue.map(f => f.franchiseId);
            setSelectedFranchiseIds(ids);
            setAllFranchisesSelected(tenantFranchises.length > 0 && ids.length === tenantFranchises.length);
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option.franchiseName}
            </li>
          )}
          renderTags={(value, getTagProps) =>
            allFranchisesSelected
              ? [
                <Chip
                  key="all"
                  label={t('templateBuilder.allFranchises')}
                  size="small"
                />,
              ]
              : value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.franchiseId}
                  label={option.franchiseName}
                  size="small"
                />
              ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('templateBuilder.franchises')}
              placeholder={selectedFranchiseIds.length === 0 ? t('templateBuilder.selectFranchises') : ''}
              required
            />
          )}
        />
        {tenantFranchises.length > 1 && (
          <FormControlLabel
            control={
              <Checkbox
                checked={allFranchisesSelected}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setAllFranchisesSelected(checked);
                  setSelectedFranchiseIds(checked ? tenantFranchises.map(f => f.franchiseId) : []);
                }}
              />
            }
            label={t('templateBuilder.selectAllFranchises')}
            sx={{ mt: 1 }}
          />
        )}
        <TextField
          fullWidth
          type="number"
          label={t('templateBuilder.passingScore')}
          value={template.passingScore ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            const newPassing = val === '' ? null : Math.min(100, Math.max(0, parseFloat(val)));
            const updates: Partial<typeof template> = { passingScore: newPassing };
            if (newPassing !== null && template.amberScore !== null && template.amberScore !== undefined && template.amberScore >= newPassing) {
              updates.amberScore = null;
            }
            setTemplate({ ...template, ...updates });
          }}
          inputProps={{ min: 0, max: 100, step: 0.1 }}
          helperText={t('templateBuilder.passingScoreHelp')}
          sx={{ mt: 2 }}
        />
        <TextField
          fullWidth
          type="number"
          label={t('templateBuilder.amberScore')}
          value={template.amberScore ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            const maxAllowed = template.passingScore != null ? template.passingScore - 0.1 : 100;
            setTemplate({
              ...template,
              amberScore: val === '' ? null : Math.min(maxAllowed, Math.max(0, parseFloat(val))),
            });
          }}
          inputProps={{ min: 0, max: template.passingScore != null ? Math.max(0, template.passingScore - 0.1) : 100, step: 0.1 }}
          helperText={
            template.passingScore != null
              ? t('templateBuilder.amberScoreHelpWithMax', { max: (template.passingScore - 0.1).toFixed(1), defaultValue: `Must be less than the passing score (max ${(template.passingScore - 0.1).toFixed(1)}%). Leave empty for no amber boundary.` })
              : t('templateBuilder.amberScoreHelp')
          }
          disabled={template.passingScore == null}
          sx={{ mt: 2 }}
        />
      </Section>

      {/* Sections */}
      <Section style={{ p: 5, mb: 5 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 5 }}
        >
          <Box>
            {t('templateBuilder.sectionsCount', { count: template.sections?.length || 0 })}
          </Box>
          <Button
            variant="primary"
            size="md"
            startIcon={<AddIcon />}
            onClick={handleAddSection}
          >
            {t('templateBuilder.addSection')}
          </Button>
        </Stack>

        {template.sections && template.sections.length > 0 ? (
          <DragDropContext onDragEnd={handleSectionDragEnd}>
            <Droppable droppableId="sections">
              {(provided) => (
                <Box
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                >
                  {template.sections!.map((section, sectionIndex) => (
                    <Draggable
                      key={`section-${sectionIndex}`}
                      draggableId={`section-${sectionIndex}`}
                      index={sectionIndex}
                    >
                      {(provided, snapshot) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <Section
                            style={{
                              p: 5,
                              mb: 3,
                              boxShadow: snapshot.isDragging ? '0 1px 2px 1px #1018280D' : 'none',
                              bgcolor: snapshot.isDragging ? theme.palette.custom.gray[100] : undefined,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 2,
                                mb: 3,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                                <Box
                                  {...provided.dragHandleProps}
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  <DragIcon sx={{ color: 'text.secondary', cursor: 'grab' }} />
                                </Box>
                                <Box>
                                  <Box sx={{ fontWeight: 500, fontSize: '1rem' }}>
                                    {section.name}
                                  </Box>
                                  <Box sx={{ fontSize: '0.875rem', color: theme.palette.custom.gray[600] }}>
                                    {section.description || t('templateBuilder.noDescription')}
                                    {' • '}
                                    {section.questions?.length || 0} {(section.questions?.length || 0) !== 1 ? t('templateBuilder.questionCount_plural', { count: section.questions?.length || 0 }).split(' ').pop() : t('templateBuilder.questionCount', { count: 1 }).split(' ').pop()}
                                  </Box>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleAddQuestion(sectionIndex)}
                                  title={t('templateBuilder.addQuestion')}
                                >
                                  <AddIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditSection(sectionIndex)}
                                  title={t('templateBuilder.editSection')}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteSection(sectionIndex)}
                                  title={t('templateBuilder.deleteSection')}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Box>

                            {/* Questions for this section */}
                            {section.questions && section.questions.length > 0 ? (
                              <Box
                                sx={{
                                  pt: 3,
                                  borderTop: 1,
                                  borderColor: 'divider',
                                }}
                              >
                                <DragDropContext onDragEnd={handleQuestionDragEnd(sectionIndex)}>
                                  <Droppable droppableId={`questions-${sectionIndex}`}>
                                    {(provided) => (
                                      <Box
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                      >
                                        {section.questions!.map((question: AuditQuestion, questionIndex: number) => (
                                          <Draggable
                                            key={`question-${question.id}-${questionIndex}`}
                                            draggableId={`question-${sectionIndex}-${questionIndex}`}
                                            index={questionIndex}
                                          >
                                            {(provided, snapshot) => (
                                              <Box
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  pt: 3,
                                                  pb: 3,
                                                  borderBottom: questionIndex < section.questions!.length - 1 ? 1 : 0,
                                                  borderColor: 'divider',
                                                  bgcolor: snapshot.isDragging ? theme.palette.custom.gray[100] : 'transparent',
                                                  boxShadow: snapshot.isDragging ? '0 1px 2px 1px #1018280D' : 'none',
                                                }}
                                              >
                                                <Box
                                                  {...provided.dragHandleProps}
                                                  sx={{ mr: 1, display: 'flex', alignItems: 'center' }}
                                                >
                                                  <DragIcon sx={{ fontSize: '1rem', color: 'text.secondary', cursor: 'grab' }} />
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                  <Box sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                                    Q{questionIndex + 1}: {question.questionText}
                                                  </Box>
                                                  <Box sx={{ fontSize: '0.75rem', color: theme.palette.custom.gray[600], mt: 0.5 }}>
                                                    Type: {QuestionType[question.questionType]}
                                                    {question.maxScore !== null && question.maxScore !== undefined && ` • ${t('templateBuilder.maxScore')}: ${question.maxScore}`}
                                                    {question.isRequired && ` • ${t('common.required')}`}
                                                  </Box>
                                                </Box>
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleEditQuestion(sectionIndex, questionIndex)}
                                                  title={t('templateBuilder.editQuestion')}
                                                >
                                                  <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleDeleteQuestion(sectionIndex, questionIndex)}
                                                  title={t('templateBuilder.deleteQuestion')}
                                                >
                                                  <DeleteIcon fontSize="small" />
                                                </IconButton>
                                              </Box>
                                            )}
                                          </Draggable>
                                        ))}
                                        {provided.placeholder}
                                      </Box>
                                    )}
                                  </Droppable>
                                </DragDropContext>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  pt: 3,
                                  borderTop: 1,
                                  borderColor: 'divider',
                                  color: theme.palette.custom.gray[600],
                                  fontSize: '0.875rem',
                                }}
                              >
                                {t('templateBuilder.noQuestionsYet')}
                              </Box>
                            )}
                          </Section>
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: theme.palette.custom.gray[600],
            }}
          >
            {t('templateBuilder.noSectionsYet')}
          </Box>
        )}
      </Section>

      {/* Section Dialog */}
      <Dialog
        open={sectionDialogOpen}
        onClose={() => setSectionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {currentSectionIndex >= 0 ? t('templateBuilder.editSection') : t('templateBuilder.addSection')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('templateBuilder.sectionName')}
            value={editingSection?.name || ''}
            onChange={(e) => setEditingSection({ ...editingSection!, name: e.target.value })}
            required
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('common.description')}
            value={editingSection?.description || ''}
            onChange={(e) => setEditingSection({ ...editingSection!, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={() => setSectionDialogOpen(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveSection}
            disabled={!editingSection?.name?.trim()}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Question Dialog */}
      <Dialog
        open={questionDialogOpen}
        onClose={() => setQuestionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <DialogTitle>
            {editingQuestion?.id && editingQuestion.id !== 0 ? t('templateBuilder.editQuestion') : t('templateBuilder.addQuestion')}
          </DialogTitle>
          <FormControlLabel
            sx={{ flexDirection: 'row-reverse', paddingTop: '16px' }}
            control={
              <Switch
                checked={editingQuestion?.isRequired || false}
                onChange={(e) => setEditingQuestion({ ...editingQuestion!, isRequired: e.target.checked })}
              />
            }
            label={t('templateBuilder.requiredQuestion')}
          />
        </Box>

        <DialogContent>
          <TextField
            fullWidth
            label={t('templateBuilder.questionText')}
            value={editingQuestion?.questionText || ''}
            onChange={(e) => setEditingQuestion({ ...editingQuestion!, questionText: e.target.value })}
            required
          />
          <FormControl
            fullWidth
          >
            <InputLabel>{t('templateBuilder.questionType')}</InputLabel>
            <Select
              value={editingQuestion?.questionType || QuestionType.Text}
              onChange={(e) => {
                const newType = e.target.value as QuestionType;
                // Reset trigger values when question type changes since they may no longer be valid
                const resetTriggers = newType === QuestionType.Photo || newType === QuestionType.Signature
                  ? []
                  : (editingQuestion!.actionTriggers || []).map((t) => ({
                    ...t,
                    triggerValue: newType === QuestionType.Boolean ? false
                      : newType === QuestionType.Number ? 0
                        : '',
                  }));
                setEditingQuestion({ ...editingQuestion!, questionType: newType, actionTriggers: resetTriggers });
              }}
              label={t('templateBuilder.questionType')}
            >
              <MenuItem value={QuestionType.Text}>{t('templateBuilder.typeText')}</MenuItem>
              <MenuItem value={QuestionType.Number}>{t('templateBuilder.typeNumber')}</MenuItem>
              <MenuItem value={QuestionType.Boolean}>{t('templateBuilder.typeBoolean')}</MenuItem>
              <MenuItem value={QuestionType.SingleChoice}>{t('templateBuilder.typeSingleChoice')}</MenuItem>
              <MenuItem value={QuestionType.MultipleChoice}>{t('templateBuilder.typeMultipleChoice')}</MenuItem>
              <MenuItem value={QuestionType.Photo}>{t('templateBuilder.typePhoto')}</MenuItem>
              <MenuItem value={QuestionType.Signature}>{t('templateBuilder.typeSignature')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="number"
            label={t('templateBuilder.maxScore')}
            placeholder={t('templateBuilder.maxScorePlaceholder')}
            value={editingQuestion?.maxScore ?? ''}
            onChange={(e) => setEditingQuestion({ ...editingQuestion!, maxScore: e.target.value === '' ? null : parseFloat(e.target.value) || 0 })}
            helperText={t('templateBuilder.maxScoreHelper')}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label={t('templateBuilder.helpText')}
            value={editingQuestion?.helpText || ''}
            onChange={(e) => setEditingQuestion({ ...editingQuestion!, helpText: e.target.value })}
          />

          {/* Multiple Choice Options */}
          {(editingQuestion?.questionType === QuestionType.SingleChoice ||
            editingQuestion?.questionType === QuestionType.MultipleChoice) &&
            (
              <Section style={{ p: 5, mb: 5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 8 }}>
                  <Box sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    {t('templateBuilder.answerOptions')}
                  </Box>
                  <Button
                    variant="secondary"
                    size="md"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const options = editingQuestion.options || [];
                      options.push({
                        id: Date.now(),
                        questionId: editingQuestion.id || 0,
                        optionText: '',
                        score: 0,
                        displayOrder: options.length,
                        isNew: true,
                      });
                      setEditingQuestion({ ...editingQuestion, options });
                    }}
                  >
                    {t('templateBuilder.addOption')}
                  </Button>
                </Box>
                {editingQuestion.options && editingQuestion.options.length > 0 ? (
                  editingQuestion.options.map((option, idx) => (
                    <Box
                      key={option.id}
                      sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}
                    >
                      <TextField
                        size="small"
                        label={t('templateBuilder.optionLabel', { number: idx + 1 })}
                        value={option.optionText}
                        onChange={(e) => {
                          const options = [...(editingQuestion.options || [])];
                          options[idx].optionText = e.target.value;
                          setEditingQuestion({ ...editingQuestion, options });
                        }}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label={t('common.score')}
                        value={option.score}
                        onChange={(e) => {
                          const options = [...(editingQuestion.options || [])];
                          options[idx].score = parseFloat(e.target.value) || 0;
                          setEditingQuestion({ ...editingQuestion, options });
                        }}
                        sx={{ width: 100 }}
                      />
                      <IconButton
                        size="small"
                        sx={{ mb: 8 }}
                        onClick={() => {
                          const removedOption = editingQuestion.options![idx];
                          const options = editingQuestion.options!.filter((_, i) => i !== idx);
                          // Clear trigger values that referenced the deleted option
                          const triggers = (editingQuestion.actionTriggers || []).map((t) =>
                            t.triggerValue === removedOption.optionText ? { ...t, triggerValue: '' as string | number | boolean } : t,
                          );
                          setEditingQuestion({ ...editingQuestion, options, actionTriggers: triggers });
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))
                ) : (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 2,
                      color: theme.palette.custom.gray[600],
                      fontSize: '0.875rem',
                    }}
                  >
                    {t('templateBuilder.noOptionsYet')}
                  </Box>
                )}
              </Section>
            )}

          {/* Action Triggers - not available for Photo or Signature questions */}
          {editingQuestion?.questionType !== QuestionType.Photo &&
            editingQuestion?.questionType !== QuestionType.Signature &&
            (
              <Section style={{ p: 5, mb: 5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    {t('templateBuilder.actionTriggers')}
                  </Box>
                  <Button
                    variant="secondary"
                    size="md"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const triggers = editingQuestion?.actionTriggers || [];
                      triggers.push({
                        triggerType: ActionTriggerType.RequireNote,
                        triggerCondition: TriggerCondition.Equals,
                        triggerValue: editingQuestion?.questionType === QuestionType.Boolean ? false : '',
                        isRequired: true,
                        requiredBeforeSubmission: false,
                        promptText: '',
                      });
                      setEditingQuestion({ ...editingQuestion!, actionTriggers: triggers });
                    }}
                  >
                    {t('templateBuilder.addTrigger')}
                  </Button>
                </Box>
                <Box sx={{ fontSize: '0.75rem', color: theme.palette.custom.gray[600], mb: 5 }}>
                  {t('templateBuilder.actionTriggersDescription')}
                </Box>
                {editingQuestion?.actionTriggers && editingQuestion.actionTriggers.length > 0 ? (
                  editingQuestion.actionTriggers.map((trigger, idx) => (
                    <Section style={{ p: 5, mb: 5, boxShadow: 'none' }}
                      key={idx}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <FormControl size="small"
                          sx={{ flex: 1 }}>
                          <InputLabel>{t('templateBuilder.actionType')}</InputLabel>
                          <Select
                            value={trigger.triggerType}
                            onChange={(e) => {
                              const triggers = [...(editingQuestion.actionTriggers || [])];
                              triggers[idx].triggerType = Number(e.target.value);
                              setEditingQuestion({ ...editingQuestion, actionTriggers: triggers });
                            }}
                            label={t('templateBuilder.actionType')}
                          >
                            <MenuItem value={ActionTriggerType.RequireNote}>{t('templateBuilder.requireNote')}</MenuItem>
                            <MenuItem value={ActionTriggerType.RequirePhoto}>{t('templateBuilder.requirePhoto')}</MenuItem>
                            <MenuItem value={ActionTriggerType.CreateActionItem}>{t('templateBuilder.createActionItem')}</MenuItem>
                            <MenuItem value={ActionTriggerType.RequireSignature}>{t('templateBuilder.requireSignature')}</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl size="small"
                          sx={{ width: 120 }}>
                          <InputLabel>{t('templateBuilder.condition')}</InputLabel>
                          <Select
                            value={trigger.triggerCondition}
                            onChange={(e) => {
                              const triggers = [...(editingQuestion.actionTriggers || [])];
                              triggers[idx].triggerCondition = Number(e.target.value);
                              setEditingQuestion({ ...editingQuestion, actionTriggers: triggers });
                            }}
                            label={t('templateBuilder.condition')}
                          >
                            <MenuItem value={TriggerCondition.Equals}>{t('templateBuilder.equals')}</MenuItem>
                            <MenuItem value={TriggerCondition.NotEquals}>{t('templateBuilder.notEquals')}</MenuItem>
                            {editingQuestion.questionType === QuestionType.Number && (
                              <MenuItem value={TriggerCondition.LessThan}>{t('templateBuilder.lessThan')}</MenuItem>
                            )}
                            {editingQuestion.questionType === QuestionType.Number && (
                              <MenuItem value={TriggerCondition.GreaterThan}>{t('templateBuilder.greaterThan')}</MenuItem>
                            )}
                            {(editingQuestion.questionType === QuestionType.Text || editingQuestion.questionType === QuestionType.MultipleChoice) && (
                              <MenuItem value={TriggerCondition.Contains}>{t('templateBuilder.contains')}</MenuItem>
                            )}
                          </Select>
                        </FormControl>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {editingQuestion.questionType === QuestionType.Boolean ? (
                          <FormControl size="small"
                            sx={{ flex: 1 }}>
                            <InputLabel>{t('templateBuilder.triggerValue')}</InputLabel>
                            <Select
                              value={trigger.triggerValue.toString()}
                              onChange={(e) => {
                                const triggers = [...(editingQuestion.actionTriggers || [])];
                                triggers[idx].triggerValue = e.target.value === 'true';
                                setEditingQuestion({ ...editingQuestion, actionTriggers: triggers });
                              }}
                              label={t('templateBuilder.triggerValue')}
                            >
                              <MenuItem value="true">{t('common.yes')}</MenuItem>
                              <MenuItem value="false">{t('common.no')}</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (editingQuestion.questionType === QuestionType.SingleChoice ||
                          editingQuestion.questionType === QuestionType.MultipleChoice) ?
                          (
                            <FormControl size="small"
                              sx={{ flex: 1 }}
                              error={!!(trigger.triggerValue && !(editingQuestion.options || []).some((o) => o.optionText === trigger.triggerValue))}
                            >
                              <InputLabel>{t('templateBuilder.triggerValue')}</InputLabel>
                              <Select
                                value={trigger.triggerValue}
                                onChange={(e) => {
                                  const triggers = [...(editingQuestion.actionTriggers || [])];
                                  triggers[idx].triggerValue = e.target.value as string;
                                  setEditingQuestion({ ...editingQuestion, actionTriggers: triggers });
                                }}
                                label={t('templateBuilder.triggerValue')}
                              >
                                {editingQuestion.options?.map((option) => (
                                  <MenuItem key={option.id}
                                    value={option.optionText}>
                                    {option.optionText || t('templateBuilder.optionLabel', { number: option.id })}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : editingQuestion.questionType === QuestionType.Number ? (
                            <TextField
                              size="small"
                              type="number"
                              label={t('templateBuilder.triggerValue')}
                              value={trigger.triggerValue}
                              onChange={(e) => {
                                const triggers = [...(editingQuestion.actionTriggers || [])];
                                triggers[idx].triggerValue = parseFloat(e.target.value) || 0;
                                setEditingQuestion({ ...editingQuestion, actionTriggers: triggers });
                              }}
                              sx={{ flex: 1 }}
                            />
                          ) : (
                            <TextField
                              size="small"
                              label={t('templateBuilder.triggerValue')}
                              value={trigger.triggerValue}
                              onChange={(e) => {
                                const triggers = [...(editingQuestion.actionTriggers || [])];
                                triggers[idx].triggerValue = e.target.value;
                                setEditingQuestion({ ...editingQuestion, actionTriggers: triggers });
                              }}
                              sx={{ flex: 1 }}
                            />
                          )}
                        <IconButton
                          size="small"
                          sx={{ mb: 8 }}
                          onClick={() => {
                            const triggers = editingQuestion.actionTriggers!.filter((_, i) => i !== idx);
                            setEditingQuestion({ ...editingQuestion, actionTriggers: triggers });
                          }}
                          title="Remove trigger"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      <TextField
                        size="small"
                        label={t('templateBuilder.promptText')}
                        value={trigger.promptText || ''}
                        onChange={(e) => {
                          const triggers = [...(editingQuestion.actionTriggers || [])];
                          triggers[idx].promptText = e.target.value;
                          setEditingQuestion({ ...editingQuestion, actionTriggers: triggers });
                        }}
                        placeholder={t('templateBuilder.promptTextPlaceholder')}
                        helperText={t('templateBuilder.promptTextHelper')}
                        fullWidth
                      />

                      {trigger.triggerType === ActionTriggerType.CreateActionItem && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: theme.palette.custom.gray[50], borderRadius: 1 }}>
                          <Box sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 5 }}>
                            {t('templateBuilder.actionItemConfig')}
                          </Box>
                          <TextField
                            size="small"
                            label={t('templateBuilder.actionItemTitle')}
                            value={trigger.actionItemTitle || ''}
                            onChange={(e) => {
                              const triggers = [...(editingQuestion.actionTriggers || [])];
                              triggers[idx].actionItemTitle = e.target.value;
                              setEditingQuestion({ ...editingQuestion, actionTriggers: triggers });
                            }}
                            placeholder={t('templateBuilder.actionItemTitlePlaceholder')}
                            fullWidth
                            sx={{ mb: 5 }}
                          />
                          <TextField
                            size="small"
                            label={t('templateBuilder.actionItemDescription')}
                            value={trigger.actionItemDescription || ''}
                            onChange={(e) => {
                              const triggers = [...(editingQuestion.actionTriggers || [])];
                              triggers[idx].actionItemDescription = e.target.value;
                              setEditingQuestion({ ...editingQuestion, actionTriggers: triggers });
                            }}
                            placeholder={t('templateBuilder.actionItemDescriptionPlaceholder')}
                            multiline
                            rows={2}
                            fullWidth
                            sx={{ mb: 5 }}
                          />
                          <Box sx={{ display: 'flex', gap: 5 }}>
                            <TextField
                              size="small"
                              select
                              label={t('templateBuilder.actionItemPriority')}
                              value={trigger.actionItemPriority || 'High'}
                              onChange={(e) => {
                                const triggers = [...(editingQuestion.actionTriggers || [])];
                                triggers[idx].actionItemPriority = e.target.value as 'Low' | 'Medium' | 'High' | 'Critical';
                                setEditingQuestion({ ...editingQuestion, actionTriggers: triggers });
                              }}
                              sx={{ flex: 1 }}
                            >
                              <MenuItem value="Low">{t('common.low')}</MenuItem>
                              <MenuItem value="Medium">{t('common.medium')}</MenuItem>
                              <MenuItem value="High">{t('common.high')}</MenuItem>
                              <MenuItem value="Critical">{t('common.critical')}</MenuItem>
                            </TextField>
                            <TextField
                              size="small"
                              type="number"
                              label={t('templateBuilder.actionItemDueWeeks')}
                              value={trigger.actionItemDueWeeks ?? 4}
                              onChange={(e) => {
                                const triggers = [...(editingQuestion.actionTriggers || [])];
                                triggers[idx].actionItemDueWeeks = Math.max(1, parseInt(e.target.value) || 1);
                                setEditingQuestion({ ...editingQuestion, actionTriggers: triggers });
                              }}
                              inputProps={{ min: 1, max: 52 }}
                              helperText={t('templateBuilder.actionItemDueWeeksHelper')}
                              sx={{ flex: 1 }}
                            />
                          </Box>
                        </Box>
                      )}

                      <FormControlLabel
                        control={
                          <Switch
                            checked={trigger.isRequired}
                            onChange={(e) => {
                              const triggers = [...(editingQuestion.actionTriggers || [])];
                              triggers[idx].isRequired = e.target.checked;
                              setEditingQuestion({ ...editingQuestion, actionTriggers: triggers });
                            }}
                            size="small"
                          />
                        }
                        label={t('templateBuilder.requiredAction')}
                      />
                    </Section>
                  ))
                ) : (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 2,
                      color: theme.palette.custom.gray[600],
                      fontSize: '0.875rem',
                    }}
                  >
                    {t('templateBuilder.noTriggersYet')}
                  </Box>
                )}
              </Section>
            )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => setQuestionDialogOpen(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleSaveQuestion}
          >
            {t('templateBuilder.saveQuestion')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default AuditTemplateBuilderPage;